import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for frontend error tracking
 * Set REACT_APP_SENTRY_DSN environment variable to enable
 */
export function initSentry() {
  // Only initialize if SENTRY_DSN is set
  if (!process.env.REACT_APP_SENTRY_DSN) {
    console.log('Sentry not initialized - REACT_APP_SENTRY_DSN not set');
    return;
  }

  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV || 'development',

    // Set tracesSampleRate to 1.0 to capture 100% of transactions
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

    // Session Replay
    replaysSessionSampleRate: 0.1, // Sample 10% of sessions
    replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors

    // Filter out sensitive data
    beforeSend(event, hint) {
      // Remove passwords from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
          if (breadcrumb.data && breadcrumb.data.password) {
            breadcrumb.data.password = '[Filtered]';
          }
          return breadcrumb;
        });
      }

      return event;
    },

    // Ignore certain errors
    ignoreErrors: [
      // Network errors
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      // ResizeObserver errors (benign)
      'ResizeObserver loop limit exceeded',
      // Chrome extensions
      'Non-Error promise rejection captured',
    ],
  });

  console.log(`Sentry initialized for ${process.env.NODE_ENV || 'development'} environment`);
}

/**
 * Manually capture an exception
 */
export function captureException(error, context = {}) {
  if (!process.env.REACT_APP_SENTRY_DSN) {
    console.error('Error (Sentry not configured):', error);
    return;
  }

  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture a message
 */
export function captureMessage(message, level = 'info', context = {}) {
  if (!process.env.REACT_APP_SENTRY_DSN) {
    console.log(`Message (Sentry not configured): ${message}`);
    return;
  }

  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
}

/**
 * Set user context
 */
export function setUser(user) {
  if (!process.env.REACT_APP_SENTRY_DSN) return;

  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: `${user.first_name} ${user.last_name}`,
    role: user.role,
  });
}

/**
 * Clear user context
 */
export function clearUser() {
  if (!process.env.REACT_APP_SENTRY_DSN) return;
  Sentry.setUser(null);
}

export default Sentry;
